/**
 * This file is business logic that is related to gateway cards for both the team cards and personal cards pages.
 *
 * It's not clear if there is a better way to approach this, but splitting out this logic and sharing it across both pages
 * seems to make sense.
 */
import { IGetIssuingDetailsResponse } from '@ClientApi/cardIssuing';
import { LinkedAccountsApiService } from '@ClientApi/linkedAccounts';
import { EMBURSE_ISSUED_CARDS, INSTITUTION_TYPE } from '@Shared/constants';
import {
  AccountCapabilities,
  AccountCapabilitiesCardAllowanceIntervalEnum,
  AccountCapabilitiesCardStatusEnum,
  AccountCapabilitiesVirtualCardStatusEnum,
  CardAllowance,
  CardType,
  EmburseCardsCardJson,
  INTERVAL_LONG_DISPLAY_NAMES
} from '@Shared/interfaces/CardsGatewayInterfaces';
import { ILinkedAccountModel } from '@Shared/models';

export const GATEWAY_PROVIDER = 'emburse_cards_gateway';

interface GatewayObserver {
  update(): void;
}

// Caching state at the module level is not ideal, but the alternative is wrapping the whole thing
// in a closure or using a class-based approach. Both better IMO but more difficult to pull off
// without breaking something else in the legacy angular app.
const cardProgramCapabilitiesById = new Map<string, AccountCapabilities>();
const observers: GatewayObserver[] = [];

// Used to prevent over-fetching when fetching card capabilities for a list of cards.
const cardProgramCapabilitiesIsLoading = new Set();

export function getCardProgramCapabilitiesById() {
  return cardProgramCapabilitiesById;
}

export function getCardProgramCapabilities(cardProgramId: string) {
  if (!cardProgramId) {
    throw new Error('Invalid card program ID for fetching capabilities');
  }

  return cardProgramCapabilitiesById.get(cardProgramId);
}

/**
 * Fetch the capabilities associated with a card program and cache for later use.
 */
export async function fetchCardProgramCapabilities(cardProgramId: string) {
  if (!cardProgramId) {
    throw new Error('Invalid card program ID for fetching capabilities');
  }

  if (!cardProgramCapabilitiesById.has(cardProgramId) && !cardProgramCapabilitiesIsLoading.has(cardProgramId)) {
    cardProgramCapabilitiesIsLoading.add(cardProgramId);
    const capabilities = await LinkedAccountsApiService.getAccountCapabilities(cardProgramId);
    cardProgramCapabilitiesById.set(cardProgramId, capabilities);
    cardProgramCapabilitiesIsLoading.delete(cardProgramId);
    notifyObservers();
  }
}

/**
 * Primarily used for test purposes.
 */
export function resetCardProgramCapabilitiesMap() {
  cardProgramCapabilitiesById.clear();
  cardProgramCapabilitiesIsLoading.clear();
  notifyObservers();
}

export function isGatewayCardOrProgram(cardOrProgram: ILinkedAccountModel | IGetIssuingDetailsResponse) {
  return cardOrProgram?.provider === GATEWAY_PROVIDER;
}

export function cardIsGatewayIssuingEnabled(
  card: ILinkedAccountModel | IGetIssuingDetailsResponse,
  programCapabilities?: AccountCapabilities
): boolean {
  if (!isGatewayCardOrProgram(card)) {
    return false;
  }

  const capabilities = programCapabilities || getCardProgramCapabilities(card.parent_linked_account_id);
  if (!capabilities) {
    return false;
  }

  if (card.is_virtual_card) {
    return capabilities.issuing.virtual;
  } else if (card.is_ghost) {
    return capabilities.issuing.ghost;
  }

  return capabilities.issuing.physical;
}

export function cardProgramIsGatewayIssuingEnabled(cardProgram: ILinkedAccountModel, programCapabilities?: AccountCapabilities) {
  if (!isGatewayCardOrProgram(cardProgram)) {
    return false;
  }

  const capabilities = programCapabilities || getCardProgramCapabilities(cardProgram.id);

  if (!capabilities) {
    return false;
  }

  const canIssueVirtualCards = capabilities.issuing.virtual;
  const canIssueRealCards = capabilities.issuing.ghost || capabilities.issuing.physical;
  return canIssueVirtualCards || canIssueRealCards;
}

export function cardSupportsStatusByCapability(
  card: ILinkedAccountModel | IGetIssuingDetailsResponse,
  supportedStatus: AccountCapabilitiesCardStatusEnum | AccountCapabilitiesVirtualCardStatusEnum,
  programCapabilities?: AccountCapabilities
) {
  if (!cardIsGatewayIssuingEnabled(card, programCapabilities)) {
    return false;
  }
  const capabilities = programCapabilities || getCardProgramCapabilities(card.parent_linked_account_id);
  if (!capabilities) {
    return false;
  }

  if (card.is_virtual_card) {
    return capabilities.virtualCard.status.some(
      (status: AccountCapabilitiesVirtualCardStatusEnum) => status.toLowerCase() === supportedStatus.toLowerCase()
    );
  }

  return capabilities.card.status.some(
    (status: AccountCapabilitiesCardStatusEnum) => status.toLowerCase() === supportedStatus.toLowerCase()
  );
}

export function registerObserver(observer: GatewayObserver) {
  observers.push(observer);
}

export function unregisterObserver(observer: GatewayObserver) {
  observers.splice(observers.indexOf(observer), 1);
}

function notifyObservers() {
  observers.forEach((observer) => observer.update());
}

/**
 * Gets a display value for a given interval
 * @param provider The card's provider
 * @param interval The interval.  Optional.  Will default to "non-recurring" if not provided.
 * @returns
 */
export function getCardIntervalDisplay(provider: string, interval?: string) {
  if (provider === GATEWAY_PROVIDER) {
    return INTERVAL_LONG_DISPLAY_NAMES[
      (interval || AccountCapabilitiesCardAllowanceIntervalEnum.Continuous).toLowerCase() as AccountCapabilitiesCardAllowanceIntervalEnum
    ];
  }
}

export function getCardTypeDisplay(type: string, card_type: string) {
  if (type === 'personal_card') {
    return 'personal';
  }

  return card_type.toLowerCase() === CardType.Physical ? CardType.Physical : CardType.Virtual;
}

export function getCardAllowanceAmount(provider: string, allowance?: EmburseCardsCardJson['allowance'] | CardAllowance) {
  if (provider === GATEWAY_PROVIDER) {
    return (allowance as CardAllowance)?.intervalAmount;
  }
}

export function getCardAllowanceBalance(provider: string, allowance?: EmburseCardsCardJson['allowance'] | CardAllowance) {
  if (provider === GATEWAY_PROVIDER) {
    return (allowance as CardAllowance)?.remainingBalance;
  }
}

/**
 * Gets whether a program is issuing-enabled or not.  Unlike @func cardProgramIsGatewayIssuingEnabled, this works for all providers.
 * @param program The program
 * @param capabilities Capabilities.  Optional, and unused if not a Gateway program.  If needed but not provided, will use cached capabilities if available (if not, will return false).
 * @returns
 */
export function canIssueCardsForProgram(program: ILinkedAccountModel, capabilities?: AccountCapabilities) {
  if (isGatewayCardOrProgram(program)) {
    return cardProgramIsGatewayIssuingEnabled(program, capabilities);
  }

  return program.supports_card_issuing;
}

/**
 * Check if any program supports issuing
 *
 * @export
 * @param {ILinkedAccountModel[]} cardPrograms
 * @param {Record<string, AccountCapabilities>} capabilitiesMap
 */
export function hasIssuableCardProgram(cardPrograms: ILinkedAccountModel[], capabilitiesMap: Record<string, AccountCapabilities>) {
  return cardPrograms.some((cardProgram) => {
    const capabilitiesForProgram = capabilitiesMap[cardProgram.id];
    return capabilitiesForProgram && canIssueCardsForProgram(cardProgram, capabilitiesForProgram);
  });
}

export function isAmexGatewayProgram(card: ILinkedAccountModel) {
  if (
    ([INSTITUTION_TYPE.AMEX_CPG, INSTITUTION_TYPE.AMEX_CEL, INSTITUTION_TYPE.AMEX_FINS, INSTITUTION_TYPE.AMEX_FILES] as string[]).includes(
      card.institution_type
    )
  ) {
    return true;
  }

  return false;
}

/**
 * Gets whether a card is supported by the card details modal or page
 * @param card The card
 * @param capabilities Capabilities.  Optional, and unused if not a Gateway card.  If needed but not provided, will use cached capabilities if available (if not, will return false).
 * @returns
 */
export function canViewDetailsForCard(card: ILinkedAccountModel, capabilities?: AccountCapabilities) {
  if (isGatewayCardOrProgram(card)) {
    if (isAmexGatewayProgram(card)) {
      return true;
    }
    return cardIsGatewayIssuingEnabled(card, capabilities);
  }
}

export function isEmburseIssuedCard(issuingDetails: IGetIssuingDetailsResponse) {
  return isGatewayCardOrProgram(issuingDetails) && EMBURSE_ISSUED_CARDS.includes(issuingDetails.institution_type);
}
